/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect } from "react";

import { Controller } from "react-hook-form";

import { ComboBox, Icon, Label, TooltipHost } from "@fluentui/react";

import { FlexCol, FlexRow } from "Components/Common/Layout/Layout";
import { HookErrorMessage } from "Components/ReactHookForm/HookErrorMessage/HookErrorMessage";

import { comboBoxStyles, getIconStyles } from "./ComboBoxController.styles";
import { IComboBoxControllerProps } from "./IComboBoxControllerProps";

/**
 * ComboBoxController.
 * @component
 * @param {IComboBoxControllerProps} props The component props.
 * @returns {React.ReactElement} ComboBoxController
 */
const ComboBoxController: (props: IComboBoxControllerProps) => React.ReactElement = (
  props: IComboBoxControllerProps
): React.ReactElement => {
  const {
    autoComplete,
    control,
    defaultValue,
    errors,
    hasTooltip,
    inputName,
    isDisabled,
    isRequired,
    labelStyles,
    labelDisplayName,
    placeholderText,
    toolTipDisplayText,
    onChange,
    options,
    defaultSelectedKey,
  } = props;

  useEffect(() => {
    /**
     * Accessibility fix - add aria label to combobox
     * element of TagPicker component.
     */
    const comboboxElement = Array.from(document.getElementsByClassName("ms-ComboBox-Input"));

    const addAriaLabel = document.createAttribute("aria-label");
    addAriaLabel.value = props.labelDisplayName || props.inputName;

    if (comboboxElement && comboboxElement.length > 0) {
      comboboxElement[0].setAttributeNode(addAriaLabel);
    }
  }, []);

  return (
    <FlexCol>
      <FlexRow>
        <Label htmlFor={inputName} required={isRequired} styles={labelStyles}>
          {labelDisplayName}
        </Label>
        {hasTooltip && (
          <TooltipHost id={inputName} content={toolTipDisplayText || labelDisplayName}>
            <Icon
              aria-label="information"
              iconName="info"
              role="img"
              styles={getIconStyles(!!isRequired)}
              tabIndex={0}
            />
          </TooltipHost>
        )}
      </FlexRow>
      <FlexRow>
        <Controller
          aria-label={labelDisplayName}
          name={inputName}
          control={control}
          render={({ field }): React.ReactElement => (
            <ComboBox
              aria-label={labelDisplayName}
              {...props}
              defaultValue={defaultValue}
              defaultSelectedKey={defaultSelectedKey}
              placeholder={placeholderText}
              styles={comboBoxStyles}
              useComboBoxAsMenuWidth={true}
              scrollSelectedToTop={true}
              autoComplete={autoComplete}
              selectedKey={field.value}
              onChange={(_, option, index, value): void => {
                field.onChange(option?.key);
                !!onChange && !!option && onChange(option, index, value);
              }}
              options={options}
              disabled={isDisabled}
            />
          )}
        />
      </FlexRow>
      <HookErrorMessage errors={errors} inputName={inputName} />
    </FlexCol>
  );
};

export default ComboBoxController;
